import type { SRT_SortingState } from '@/shared/components/master-table'
import type { OptimizationModel } from '@/shared/generated'
import type { TFunction } from 'i18next'

import React, { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { all, any, filter, prop } from 'lodash/fp'
import { ExternalLinkIcon, InfoIcon, RotateCcwIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	getOptimizationsMap,
	getOptStatus,
	getStatusBadgeVariant,
	useCheckOptimizationForDeleteExport,
	useOptimizationsQuery,
	useResetOptimizationMutation,
} from '@/entities/optimization'
import { useOptimizationGroupsQuery } from '@/entities/optimization-group'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import {
	createSRTColumnHelper,
	MasterTable,
	useMasterReactTable,
} from '@/shared/components/master-table'
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import { linkButtonVariants } from '@/shared/components/ui/link-button'
import {
	Modal,
	ModalBody,
	ModalContent,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Spinner } from '@/shared/components/ui/spinner'
import { Tag } from '@/shared/components/ui/tag'
import { OptimizationStatus } from '@/shared/generated'
import { getMasterTableLocale } from '@/shared/i18n'
import { ampli } from '@/shared/lib/ampli'

import { sortOptimizationGroupsByStatus } from '../lib/helpers'

type AffectedOptimizationGroupsModalProps = {
	isOpen: boolean
	close: () => void
	title: string
	subtitle: string
	onConfirm?: () => void
	onCancel?: () => void
} & (
	| {
			getOptimizationGroupsIds: (() => Promise<string[]>) | (() => string[])
			optimizationGroupIds?: never
			errorMessages?: never
	  }
	| {
			optimizationGroupIds: string[]
			getOptimizationGroupsIds?: never
			// TODO: Display error messages in UI when design/UX spec is available
			// Currently accepted but not displayed - deferred until UX requirements defined
			errorMessages?: Record<string, string>
	  }
)
export function AffectedOptimizationGroupsModal({
	isOpen,
	close,
	getOptimizationGroupsIds,
	optimizationGroupIds,
	errorMessages,
	title,
	subtitle,
	onConfirm,
	onCancel,
}: AffectedOptimizationGroupsModalProps) {
	const { t } = useTranslation()

	const idsQuery = useQuery<string[]>({
		queryKey: ['affected-optimization-groups-ids'],
		queryFn: getOptimizationGroupsIds!,
		refetchOnMount: 'always',
		enabled: !!getOptimizationGroupsIds && !optimizationGroupIds,
	})
	const ids = optimizationGroupIds || idsQuery.data || []
	const optimizationGroupsQuery = useOptimizationGroupsQuery({
		select: filter(({ id }) => ids.includes(id)),
	})
	const optimizationsQuery = useOptimizationsQuery({
		select: (data) =>
			getOptimizationsMap(
				data.filter(({ optimization_group_id }) =>
					ids.includes(optimization_group_id)
				)
			),
	})
	const data = useMemo(
		() =>
			sortOptimizationGroupsByStatus(
				optimizationGroupsQuery.data?.map((og) => ({
					id: og.id,
					name: og.name,
					status: getOptStatus(optimizationsQuery.data?.[og.id]?.status),
				})) || []
			),
		[optimizationsQuery, optimizationGroupsQuery]
	)
	const isCorrectStatuses = all(
		({ status }) =>
			[OptimizationStatus.New, OptimizationStatus.Pending].includes(status!),
		data
	)
	const isLoading = any(prop('isFetching'), [
		optimizationGroupsQuery,
		optimizationsQuery,
		idsQuery,
	])
	const isError = any(prop('isError'), [
		optimizationGroupsQuery,
		optimizationsQuery,
		idsQuery,
	])
	const handleCancel = () => {
		onCancel?.()
		close()
	}

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					handleCancel()
				}
			}}
		>
			<ModalContent className='max-w-5xl' data-testid='affected-ogs-modal'>
				<ModalHeader>
					<ModalTitle data-testid='affected-ogs-modal-title'>
						{title}
					</ModalTitle>
					<ModalDescription data-testid='affected-ogs-modal-subtitle'>
						{subtitle}
					</ModalDescription>
				</ModalHeader>
				<ErrorBoundary>
					<ModalBody className='space-y-3'>
						{!isCorrectStatuses && (
							<Alert variant='warning' data-testid='affected-ogs-warning-alert'>
								<InfoIcon />
								<AlertTitle>
									{t('general_action_blocked', {
										defaultValue: 'Action blocked',
									})}
								</AlertTitle>
								<AlertDescription>
									{t('global.pc.cant_be_changed.hint', {
										defaultValue:
											'You cant procceed because some of the optimizations are not in NEW or PENDING status.',
									})}
								</AlertDescription>
							</Alert>
						)}
						{isError && (
							<Alert
								variant='destructive'
								data-testid='affected-ogs-error-alert'
							>
								<InfoIcon />
								<AlertTitle>{t('fatal_error_title')}</AlertTitle>
								<AlertDescription>
									{t('fatal_error_desc', { defaultValue: 'Error' })}
								</AlertDescription>
							</Alert>
						)}
						<OptimizationGroupsDataTable isLoading={isLoading} data={data} />
					</ModalBody>
					<ModalFooter>
						{onConfirm ? (
							<Button
								className='ml-auto'
								onClick={handleCancel}
								data-testid='affected-ogs-cancel-button'
							>
								{t('general_cancel')}
							</Button>
						) : (
							<Button
								className='ml-auto'
								onClick={handleCancel}
								data-testid='affected-ogs-cancel-button'
							>
								{t('general_close')}
							</Button>
						)}
						{onConfirm && (
							<Button
								variant='primary'
								disabled={!isCorrectStatuses || isLoading || isError}
								onClick={() => {
									ampli.globalPcConfirmationModalConfirm()
									onConfirm()
									close()
								}}
								data-testid='affected-ogs-confirm-button'
							>
								{t('general_next')}
							</Button>
						)}
					</ModalFooter>
				</ErrorBoundary>
			</ModalContent>
		</Modal>
	)
}
type TabelDataRow = {
	id: string
	name: string
	status?: OptimizationStatus
}
function OptimizationGroupsDataTable({
	data,
	isLoading,
}: {
	data: TabelDataRow[]
	isLoading: boolean
}) {
	return (
		<div
			className='border-base flex h-96 flex-1 flex-col overflow-hidden rounded-lg border border-solid'
			data-testid='affected-ogs-table'
		>
			<OptimizationGroupsTable isLoading={isLoading} data={data} />
		</div>
	)
}
function OptimizationGroupsTable({
	data,
	isLoading,
}: {
	data: TabelDataRow[]
	isLoading: boolean
}) {
	const { t } = useTranslation()

	const [sorting, setSorting] = useState<SRT_SortingState>([])
	const table = useMasterReactTable<TabelDataRow>({
		localization: getMasterTableLocale(),
		data,
		columns: getColumns(t),
		state: {
			sorting,
			isLoading,
		},
		defaultColumn: {
			minSize: 55,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},
		enableVirtualization: true,
		enableHeaderTooltip: true,
		defaultRowHeight: 42,
		layoutMode: 'grid',
		enableColumnActions: false,
		divideX: false,
		onSortingChange: setSorting,
		shadcnTableBodyRowProps: ({ staticRowIndex }) =>
			({
				'data-testid': `affected-og-row-${staticRowIndex}`,
			}) as React.HTMLAttributes<HTMLTableRowElement>,
	})

	return <MasterTable table={table} />
}
const columnHelper = createSRTColumnHelper<TabelDataRow>()
const getColumns = (t: TFunction) => [
	columnHelper.accessor('name', {
		size: 550,
		header: t('optimization_group'),
		Cell: ({ cellValue, row }) => (
			<Link
				target='_blank'
				className={linkButtonVariants({ variant: 'brand' })}
				onClick={() => ampli.globalPcConfirmationModalNavigateToOg()}
				to='/p/og/$optimizationGroupId/products'
				params={{ optimizationGroupId: row.original.id }}
				data-testid='affected-og-name-link'
			>
				{cellValue}
				<ExternalLinkIcon />
			</Link>
		),
	}),
	columnHelper.accessor('id', {
		size: 100,
		header: '',
		Cell: (info) => (
			<div className='flex items-center justify-end space-x-2'>
				<RowAction raw={info.row.original} />
			</div>
		),
	}),
	columnHelper.accessor('status', {
		size: 130,
		header: t('app.status'),
		Cell: ({ cellValue }) => {
			const { t } = useTranslation()

			const value = cellValue

			return (
				<div className='flex items-center space-x-2'>
					<Tag
						variant={getStatusBadgeVariant(value)}
						data-testid='affected-og-status-tag'
					>
						{t(`opt_${value}`)}
					</Tag>
				</div>
			)
		},
	}),
]
function RowAction({ raw }: { raw: TabelDataRow }) {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const checkExport = useCheckOptimizationForDeleteExport(raw.id)
	const resetOptimization = useResetOptimizationMutation({
		onSuccess: (optimization) => {
			queryClient.setQueryData(
				['optimizations', optimization.optimization_group_id],
				optimization
			)
			queryClient.setQueryData<OptimizationModel[]>(
				['optimizations'],
				(optimizations) => [
					...(optimizations || []).filter(
						(o) =>
							o.optimization_group_id !== optimization.optimization_group_id
					),
					optimization,
				]
			)
		},
		onMutate() {
			ampli.globalPcConfirmationModalRevokeOg({
				status: raw.status ?? '',
			})
		},
	})
	if (
		raw.status &&
		[
			OptimizationStatus.Finished,
			OptimizationStatus.Failed,
			OptimizationStatus.UpdatingFailed,
		].includes(raw.status)
	) {
		return (
			<Button
				size='sm'
				disabled={resetOptimization.isPending}
				onClick={() => resetOptimization.mutate(raw.id)}
				data-testid='affected-og-revoke-button'
			>
				{resetOptimization.isPending ? <Spinner /> : <RotateCcwIcon />}
				{t('general_revoke', { defaultValue: 'Revoke' })}
			</Button>
		)
	}
	if (raw.status === OptimizationStatus.Locked) {
		return (
			<Button
				size='sm'
				disabled={resetOptimization.isPending}
				onClick={checkExport(() => resetOptimization.mutate(raw.id))}
				data-testid='affected-og-revoke-button'
			>
				{resetOptimization.isPending ? <Spinner /> : <RotateCcwIcon />}
				{t('general_revoke', { defaultValue: 'Revoke' })}
			</Button>
		)
	}

	return null
}
